import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { firestore } from '../../database/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import toast from 'react-hot-toast';
import PasswordResetOtpInputPrompt from '../../components/auth/PasswordResetOtpInputPrompt';
import PasswordResetFormPrompt from '../../components/auth/PasswordResetFormPrompt';
const initialForm = {
  email: '',
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
};

const ForgotPassword = ({ open = true, onClose, onSubmit, onBackToLogin }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [showOtpInputPrompt, setShowOtpInputPrompt] = useState(false);
  const [showPasswordResetPrompt, setShowPasswordResetPrompt] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [otpRequestId, setOtpRequestId] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [errorPopup, setErrorPopup] = useState({ open: false, title: 'Reset error', message: '' });

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const checkUserExistsInFirestore = async email => {
    const trimmedEmail = email.trim();
    const normalizedEmail = trimmedEmail.toLowerCase();
    // Include all collections from App.js routes: clients, admin, alumni, coaches, moderator, s_admin
    const collections = ['clients', 'admin', 'alumni', 'coaches', 'moderator', 's_admin'];

    const checks = collections.map(async collectionName => {
      try {
        // Try with normalized (lowercase) email first (most common case)
        const q1 = query(
          collection(firestore, collectionName),
          where('email', '==', normalizedEmail)
        );
        const snapshot1 = await getDocs(q1);
        if (snapshot1.size > 0) return true;

        if (normalizedEmail !== trimmedEmail) {
          const q2 = query(
            collection(firestore, collectionName),
            where('email', '==', trimmedEmail)
          );
          const snapshot2 = await getDocs(q2);
          if (snapshot2.size > 0) return true;
        }

        return false;
      } catch (error) {
        console.error(`Error checking collection ${collectionName}:`, error);
        return false;
      }
    });

    const results = await Promise.all(checks);
    return results.some(exists => exists === true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const email = (form.email || '').trim();

      // Step 1: Validate email format
      if (!email) {
        toast.error('Please enter your email address.');
        setLoading(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error('Please enter a valid email address.');
        setLoading(false);
        return;
      }

      // Step 2: Check if email exists in system
      const userExists = await checkUserExistsInFirestore(email);
      if (!userExists) {
        toast.error('Email address not found.');
        setLoading(false);
        return;
      }

      // Step 3: Email exists - show OTP input prompt (Popover 1)
      // OTP will be sent automatically when component mounts
      setVerifiedEmail(email);
      setShowOtpInputPrompt(true);
      setLoading(false);
    } catch (error) {
      toast.error('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleOtpVerified = ({
    otpRequestId: verifiedOtpRequestId,
    email: verifiedEmail,
    otp: verifiedOtp,
  }) => {
    // OTP verified successfully - show password reset form (Popover 2)
    setOtpRequestId(verifiedOtpRequestId);
    setOtpCode(verifiedOtp); // Store the OTP code that was verified
    setShowOtpInputPrompt(false);
    setShowPasswordResetPrompt(true);
  };

  const handleOtpInputCancel = () => {
    setShowOtpInputPrompt(false);
    setVerifiedEmail('');
    setOtpRequestId(null);
    setOtpCode('');
  };

  const handlePasswordResetSuccess = () => {
    // Password reset successful - redirect to login
    setShowPasswordResetPrompt(false);
    setVerifiedEmail('');
    setOtpRequestId(null);
    setOtpCode('');
    setForm(initialForm);
    if (onBackToLogin) {
      onBackToLogin();
    }
  };

  const handlePasswordResetCancel = () => {
    setShowPasswordResetPrompt(false);
    setVerifiedEmail('');
    setOtpRequestId(null);
    setOtpCode('');
  };

  const handleClose = () => {
    if (!loading && onClose) {
      onClose();
    }
  };

  const handleBackToLogin = () => {
    if (!loading && onBackToLogin) {
      onBackToLogin();
    }
  };

  if (!open) return null;

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key="forgot-password-backdrop"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <motion.div
            key="forgot-password-modal"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative max-h-[90vh] w-[92%] max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl sm:w-full"
          >
            <div className="p-6 md:p-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.1 }}
                className="absolute right-4 top-4 text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none disabled:opacity-50"
                onClick={handleClose}
                aria-label="Close"
                disabled={loading}
              >
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </motion.button>

              <div className="mb-8 text-center">
                <h2 className="mb-2 text-2xl font-bold text-blue-600 md:text-3xl">
                  Reset Password
                </h2>
                <p className="text-sm text-gray-600 md:text-base">
                  Enter your email to receive reset instructions
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="relative">
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-blue-400">
                      <svg
                        width="18"
                        height="18"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <rect x="2" y="5" width="20" height="14" rx="2" />
                        <path d="m22 5-10 7L2 5" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-gray-300 bg-blue-50/50 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-all duration-150 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter your email address"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  transition={{ duration: 0.1 }}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-green-500 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-150 hover:from-blue-600 hover:to-green-600 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 disabled:from-gray-400 disabled:to-gray-500 disabled:hover:scale-100 md:px-6 md:py-4 md:text-base"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-3">
                      <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                      <span>Sending reset link...</span>
                    </div>
                  ) : (
                    <span className="text-lg">Send Reset Link</span>
                  )}
                </motion.button>

                <motion.button
                  type="button"
                  onClick={handleBackToLogin}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  transition={{ duration: 0.1 }}
                  className="w-full text-center text-sm text-gray-600"
                >
                  Remember your password?{' '}
                  <span className="font-medium text-blue-600 hover:text-blue-800">
                    Back to login
                  </span>
                </motion.button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {errorPopup.open && (
          <motion.div
            key="error-backdrop"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          >
            <motion.div
              key="error-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:w-full"
              role="alertdialog"
              aria-labelledby="forgot-error-title"
              aria-describedby="forgot-error-desc"
            >
              <div className="mb-4 flex items-start gap-3">
                <div className="mt-0.5 text-red-600">
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
                  </svg>
                </div>
                <div>
                  <h3 id="forgot-error-title" className="text-lg font-semibold text-gray-900">
                    {errorPopup.title}
                  </h3>
                  <p id="forgot-error-desc" className="mt-1 text-sm text-gray-700">
                    {errorPopup.message}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setErrorPopup({ ...errorPopup, open: false })}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  OK
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popover 1: OTP Input */}
      <PasswordResetOtpInputPrompt
        email={verifiedEmail}
        open={showOtpInputPrompt}
        onOtpVerified={handleOtpVerified}
        onCancel={handleOtpInputCancel}
      />

      {/* Popover 2: Password Reset Form */}
      <PasswordResetFormPrompt
        email={verifiedEmail}
        otpRequestId={otpRequestId}
        otpCode={otpCode}
        open={showPasswordResetPrompt}
        onSuccess={handlePasswordResetSuccess}
        onCancel={handlePasswordResetCancel}
      />
    </>
  );
};

export default ForgotPassword;
